import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { isAdmin } from '../utils/enums';
import { formatEventDateTime } from '../utils/formats';
import { buildPaginationQuery, getPaginatedData, scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';

// Histórico de movimientos de stock de un juego, del más reciente al más viejo.
//
// El backend lo DERIVA de las operaciones (ventas, reposiciones, transferencias, conteos):
// no hay tabla de movimientos de stock, el stock es una columna que cada operación pisa. Dos
// consecuencias que esta pantalla dice en voz alta en vez de disimular:
//   · lo que no deja fila no aparece (una edición manual del stock desde la ficha no registra nada);
//   · el saldo se reconstruye hacia atrás desde el stock de hoy, así que una edición manual
//     corre todos los saldos anteriores a ella.
// Los conteos son el ancla que delata eso: en la fecha del conteo se sabe qué decía el sistema.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

const TIPO = {
    ajuste: { label: 'Ajuste manual', cls: 'badge-dark' },
    alta: { label: 'Alta del producto', cls: 'badge-secondary' },
    usado_retirado: { label: 'Usado retirado', cls: 'badge-secondary' },
    insumo: { label: 'Insumo', cls: 'badge-info' },
    venta: { label: 'Venta', cls: 'badge-danger' },
    venta_anulada: { label: 'Venta anulada', cls: 'badge-light' },
    venta_pendiente: { label: 'Venta sin pagar', cls: 'badge-light' },
    cambio: { label: 'Cambio — devolución', cls: 'badge-info' },
    reposicion: { label: 'Reposición', cls: 'badge-success' },
    transferencia_salida: { label: 'Transferencia — salida', cls: 'badge-warning' },
    transferencia_entrada: { label: 'Transferencia — entrada', cls: 'badge-success' },
    ajuste_conteo: { label: 'Ajuste por conteo', cls: 'badge-primary' },
    conteo: { label: 'Conteo', cls: 'badge-secondary' },
};

class StockHistoryPage extends Component {
    constructor() {
        super();
        this.state = {
            checked: false,
            role: Number(localStorage.getItem('role') || 0),
            code: null,
            data: null,       // { name, currentStock, movements, total, hasMore }
            loading: false,
            error: null,
            searchInput: '',
            candidatos: [],
            buscando: false,
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        if (!role) {
            return;
        }
        // Mismo criterio que el control del conteo: acá se ve el stock del sistema y el detalle
        // de ventas. El Router no tiene guards, así que la puerta va en la pantalla.
        if (!isAdmin(role)) {
            navigateTo(localStorage.getItem('homePageURL') || '/purchase', { replace: true });
            return;
        }
        this.setState({ checked: true, role });

        const code = new URLSearchParams(this.props.location.search).get('code');
        if (code) {
            await this.loadHistory(code);
        }
    }

    // El código viaja en la URL: así el link desde el control abre el juego correcto y la
    // pantalla se puede compartir/marcar.
    componentDidUpdate(prevProps) {
        const anterior = new URLSearchParams(prevProps.location.search).get('code');
        const actual = new URLSearchParams(this.props.location.search).get('code');
        if (actual !== anterior && actual) {
            this.loadHistory(actual);
        }
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    fetchJson = async (path, options) => {
        const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
        return res.json();
    }

    loadHistory = async (code) => {
        this.setState({ loading: true, error: null, code });
        try {
            const body = await this.fetchJson(`stock/history/${code}`, { method: 'GET' });
            if (body.error === 0) {
                this.setState({ data: body.data, loading: false });
            } else {
                this.setState({ error: body.message || 'No se pudo cargar el histórico', data: null, loading: false });
            }
        } catch (e) {
            this.setState({ error: 'Error de red', data: null, loading: false });
        }
    }

    // Búsqueda del producto contra el catálogo paginado del servidor (no se baja el catálogo
    // entero para elegir uno).
    onChangeSearch = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', async () => {
            const q = search.trim();
            if (q === '') {
                this.setState({ candidatos: [], buscando: false });
                return;
            }
            this.setState({ buscando: true });
            const query = buildPaginationQuery({ page: 1, limit: 10, search: q });
            const body = await this.fetchJson(`product/all?${query}`, { method: 'GET' });
            if (body.error === 0) {
                this.setState({ candidatos: getPaginatedData(body).items || [], buscando: false });
            } else {
                this.setState({ candidatos: [], buscando: false });
            }
        }, 350);
    }

    elegir = (product) => {
        this.setState({ searchInput: '', candidatos: [] });
        navigateTo(`/stockHistory?code=${product.code}`);
    }

    renderDelta(m) {
        if (m.delta == null) {
            return <span className="badge badge-warning" title="No quedó registrado cuánto movió">?</span>;
        }
        if (Number(m.delta) === 0) {
            return <span className="text-muted">—</span>;
        }
        const positivo = Number(m.delta) > 0;
        return (
            <strong className={positivo ? 'text-success' : 'text-danger'}>
                {positivo ? `+${m.delta}` : m.delta}
            </strong>
        );
    }

    renderBuscador() {
        const { searchInput, candidatos, buscando } = this.state;
        return (
            <div className="card card-small mb-4">
                <div className="card-body">
                    <label className="m-0" htmlFor="stock-history-search"><strong>Buscar juego</strong></label>
                    <input
                        id="stock-history-search"
                        type="search"
                        className="form-control"
                        placeholder="Nombre o código"
                        value={searchInput}
                        onChange={(e) => this.onChangeSearch(e.target.value)}
                    />
                    {buscando && <small className="text-muted">Buscando…</small>}
                    {candidatos.length > 0 && (
                        <div className="list-group mt-2">
                            {candidatos.map((p) => (
                                <button
                                    type="button"
                                    key={p.code}
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => this.elegir(p)}
                                >
                                    {p.name} <small className="text-muted">· {p.code} · stock {p.stock}</small>
                                </button>
                            ))}
                        </div>
                    )}
                    {searchInput.trim() !== '' && !buscando && candidatos.length === 0 && (
                        <small className="text-muted">No hay productos que coincidan.</small>
                    )}
                </div>
            </div>
        );
    }

    renderMovimientos() {
        const { data } = this.state;
        const movs = data.movements || [];
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center flex-wrap">
                    <h6 className="m-0">
                        {data.name || data.productCode}
                        <small className="text-muted ml-2">· código {data.productCode}</small>
                    </h6>
                    <span>
                        Stock hoy: <strong style={{ fontSize: '1.2rem' }}>{data.currentStock != null ? data.currentStock : '—'}</strong>
                    </span>
                </div>
                <div className="card-body p-0 pb-3">
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0">Cuándo</th>
                                <th className="border-0">Movimiento</th>
                                <th className="border-0 text-right">Cantidad</th>
                                <th className="border-0 text-right">Stock después</th>
                                <th className="border-0">Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movs.map((m, i) => (
                                <tr key={i}>
                                    <td style={{ whiteSpace: 'nowrap' }}><small>{formatEventDateTime(m.date)}</small></td>
                                    <td>
                                        <span className={`badge ${(TIPO[m.type] || {}).cls || 'badge-light'}`}>
                                            {(TIPO[m.type] || {}).label || m.type}
                                        </span>
                                    </td>
                                    <td className="text-right">{this.renderDelta(m)}</td>
                                    <td className="text-right">
                                        {m.balanceAfter != null
                                            ? m.balanceAfter
                                            : <span className="text-muted" title="No se puede reconstruir: hay un movimiento de cantidad desconocida más arriba">—</span>}
                                    </td>
                                    <td>
                                        <small>{m.detail}</small>
                                        {m.countNote && <div><small className="text-muted">«{m.countNote}»</small></div>}
                                        {m.resolutionNote && <div><small className="text-muted">«{m.resolutionNote}» — {m.resolvedBy}</small></div>}
                                        {/* El motivo del ajuste es lo único que explica por qué el stock
                                            cambió sin una operación detrás. */}
                                        {m.adjustmentNote && <div><small className="text-muted">«{m.adjustmentNote}»</small></div>}
                                        {m.adjustedBy && <div><small className="text-muted">{m.adjustedBy}</small></div>}
                                        {m.responsibleNames && <div><small className="text-muted">Contó: {m.responsibleNames}</small></div>}
                                        {/* El conteo es la única fecha en la que se sabe qué decía el
                                            sistema. Si no coincide con la reconstrucción, hubo un
                                            movimiento que no dejó fila. */}
                                        {m.descuadre != null && (
                                            <div>
                                                <small className="text-danger">
                                                    <i className="material-icons" style={{ fontSize: '14px', verticalAlign: 'middle' }}>warning</i>{' '}
                                                    Acá el sistema decía {m.systemStock} y la reconstrucción da {m.balanceAfter}:
                                                    {' '}{Math.abs(m.descuadre)} unidad(es) se movieron sin dejar registro
                                                    (típicamente una edición manual del stock).
                                                </small>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {movs.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center text-muted p-3">
                                        Este producto no tiene movimientos registrados en esta sucursal.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="card-footer">
                    <small className="text-muted">
                        {data.hasMore
                            ? `Se muestran los ${movs.length} movimientos más recientes de ${data.total}. `
                            : `${data.total} movimiento(s). `}
                        El stock no lleva un libro de movimientos: esto se arma cruzando ventas, reposiciones,
                        transferencias y conteos, y el saldo se reconstruye hacia atrás desde el stock de hoy.
                        Desde el 08/09/2026 todos los caminos dejan registro: guardar un producto ya no
                        cambia el stock, y moverlo a mano es un ajuste con motivo obligatorio. Lo anterior
                        a esa fecha pudo cambiarse sin dejar rastro; los conteos son el punto donde eso se
                        detecta.
                    </small>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const { data, loading, error } = this.state;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={48} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Stock" subtitle="Histórico de movimientos" />
                            {error && <div className="alert alert-danger">{error}</div>}
                            {this.renderBuscador()}
                            {loading && (
                                <div className="containerLoader"><div className="loader"></div></div>
                            )}
                            {!loading && data && this.renderMovimientos()}
                            {!loading && !data && !error && (
                                <div className="alert alert-light">
                                    Buscá un juego para ver todo lo que le pasó a su stock.
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default StockHistoryPage;
